import { Link, useLocation } from 'react-router-dom';
import {
  FileText,
  LayoutDashboard,
  Coins,
  Settings,
  LogOut,
  Menu,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useCredits } from '@/features/dashboard/hooks/useCredits';
import { cn } from '@/lib/utils';

interface NavbarProps {
  onLogout: () => void;
}

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

export function Navbar({ onLogout }: NavbarProps) {
  const location = useLocation();
  const { session, user, profile } = useAuthStore();
  const isAuthenticated = !!session;
  const { toggleSidebar } = useUIStore();
  const { data: credits, isPending: creditsPending } = useCredits();

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/sources', label: 'Sources', icon: FileText },
    { to: '/team', label: 'Team', icon: Users },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4 gap-4">
        {/* Sidebar toggle (mobile + desktop) */}
        {isAuthenticated && (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={toggleSidebar}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle sidebar</span>
          </Button>
        )}

        {/* Logo */}
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2">
          <FileText className="h-6 w-6 text-indigo-600" />
          <span className="font-bold text-lg text-slate-900 hidden sm:inline-block">
            MakeMyDoc
          </span>
        </Link>

        {/* Nav links (desktop only, authenticated) */}
        {isAuthenticated && (
          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.to;
              return (
                <Link key={link.to} to={link.to}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'gap-2 text-slate-600',
                      isActive && 'bg-accent text-indigo-600 font-medium'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Button>
                </Link>
              );
            })}
          </nav>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right section */}
        {isAuthenticated && user ? (
          <div className="flex items-center gap-3">
            {/* Credits badge */}
            <Link to="/pricing">
              <Badge
                variant="secondary"
                className="gap-1.5 cursor-pointer hover:bg-secondary/80"
              >
                <Coins className="h-3.5 w-3.5 text-amber-500" />
                <span className="font-mono text-xs">
                  {creditsPending ? '—' : (credits ?? 0)}
                </span>
              </Badge>
            </Link>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={profile?.avatar_url ?? undefined}
                      alt={profile?.full_name ?? user.email ?? ''}
                    />
                    <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                      {getInitials(profile?.full_name ?? null, user.email ?? 'U')}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {profile?.full_name ?? 'User'}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" className="cursor-pointer">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="cursor-pointer">
                    <Coins className="mr-2 h-4 w-4" />
                    Buy Credits
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-rose-600 focus:text-rose-600"
                  onClick={onLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                Get Started
              </Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
